import React, { useState } from "react";
import { Row, Col, ListGroup, Card, Badge, Alert } from "react-bootstrap";

export default function TreeHelp({ defaultChapter = "important" }) {
  const [activeSection, setActiveSection] = useState(defaultChapter);

  const sections = [
    { id: "important", label: "⚠️ 0. Informații Importante" },
    { id: "configurare", label: "1. Configurare Firmă & Securitate" },
    { id: "intrari", label: "2. Intrări (Achiziții & NIR)" },
    { id: "iesiri", label: "3. Ieșiri (Vânzări & Încasări)" },
    { id: "contabilitate", label: "4. Contabilitate & Note Simple" },
    { id: "trezorerie", label: "5. Trezorerie (Casa / Banca)" },
    { id: "rapoarte", label: "6. Rapoarte & Bilanț" },
  ];

  return (
    <div className="tree-help-container py-2">
      <Row>
        {/* Sidebar cu Capitole */}
        <Col md={4} className="border-end pr-3">
          <h6 className="text-muted fw-bold mb-3 uppercase tracking-wider text-xs">Capitole Manual</h6>
          <ListGroup variant="flush" className="rounded shadow-sm">
            {sections.map((sec) => (
              <ListGroup.Item
                key={sec.id}
                action
                active={activeSection === sec.id}
                onClick={() => setActiveSection(sec.id)}
                className="d-flex justify-content-between align-items-center py-2.5 px-3 border-0 transition-all"
                style={{
                  fontSize: "0.92rem",
                  fontWeight: activeSection === sec.id ? "600" : "500",
                  borderRadius: "6px",
                  marginBottom: "4px",
                  cursor: "pointer"
                }}
              >
                {sec.label}
              </ListGroup.Item>
            ))}
          </ListGroup>
          <hr />
          <div className="small text-muted p-2 bg-light rounded border">
            <span className="fw-semibold">Scurtături taste:</span>
            <ul className="mb-0 ps-3 mt-1">
              <li><Badge bg="secondary">F1</Badge> - Deschide manualul</li>
              <li><Badge bg="secondary">Esc</Badge> - Închide manualul</li>
            </ul>
          </div>
        </Col>

        {/* Zona de Conținut Detaliat */}
        <Col md={8} className="pl-3">
          {activeSection === "important" && (
            <div>
              <h4 className="text-danger mb-3">⚠️ Informații Critice & Validări ERP</h4>
              <p className="text-muted font-italic">
                Pentru o funcționare corectă a contabilității și stocurilor, te rugăm să citești aceste reguli esențiale:
              </p>

              <Alert variant="warning" className="shadow-sm border-0 mb-4">
                <h5 className="alert-heading fw-bold">🎯 Distribuire pe Centre de Cost (Validare la Nota Contabilă)</h5>
                <p className="mb-2">
                  Când bifa <strong>"Distribuire pe centre de cost"</strong> este activată în configurarea firmei, sistemul aplică o validare strictă la salvarea facturilor (scrierea notelor contabile):
                </p>
                <ul className="mb-2 ps-3 small">
                  <li>Orice linie de document care folosește un cont de cheltuieli din <strong>clasa 6 (ex: 6022, 628, etc.)</strong> trebuie să fie distribuită pe centre de cost (Cost Centers).</li>
                  <li>Suma totală distribuită pe centrele de cost trebuie să fie <strong>exact egală</strong> cu valoarea de pe linia facturii (baza netă, fără TVA).</li>
                  <li>Dacă alocările lipsesc, sunt incomplete sau suma lor nu corespunde exact cu baza liniei, salvarea documentului va fi blocată și se va afișa o eroare de validare (de tip <em>NCValidationError</em>).</li>
                </ul>
                <p className="mb-0 small text-dark fw-bold">
                  💡 Sfat: Dacă folosești un singur centru de cost, sistemul îl poate completa automat. Dacă sunt mai multe, folosește fereastra pop-up din dreptul coloanei "Alocare centre" pentru a distribui corect sumele.
                </p>
              </Alert>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🔒 Perioade Contabile Închise</h6>
                  <p className="small text-muted mb-0">
                    Documentele (Intrări, NIR-uri, Ieșiri, Plăți/Încasări) a căror dată se află într-o perioadă închisă contabil <strong>nu mai pot fi modificate, șterse sau salvate</strong>. Pentru a face modificări, perioada trebuie redeschisă din ecranul de închidere lună de către un administrator.
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">⚡ Sincronizare Finalizare (Intrare ↔ NIR)</h6>
                  <p className="small text-muted mb-0">
                    Finalizarea unei facturi de intrare va finaliza automat și Nota de Recepție (NIR) asociată. Documentele finalizate sunt blocate pentru editare pentru a asigura corectitudinea datelor transmise în rapoarte și declarații.
                  </p>
                </Card.Body>
              </Card>
            </div>
          )}

          {activeSection === "configurare" && (
            <div>
              <h4 className="text-primary mb-3">1. Configurare Firmă & Module Active</h4>
              <p className="text-muted">
                În ecranul de editare al firmei, secțiunile <i>"1. Date Generale"</i> și <i>"5. Administrare și Module"</i> conțin o serie de bife (checkbox-uri) care controlează comportamentul întregii aplicații ERP.
              </p>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🟢 Plătitor de TVA (<code>platitor_tva</code>)</h6>
                  <p className="small text-muted mb-0">
                    Activează calculul taxei pe valoarea adăugată pentru tranzacții. Când este bifat, documentele de achiziții/vânzări vor calcula automat valoarea TVA și vor genera notele contabile corespunzătoare (conturile 4426 pentru intrări și 4427 pentru ieșiri).
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🔵 TVA la încasare (<code>tva_la_incasare</code>)</h6>
                  <p className="small text-muted mb-0">
                    Activează regimul special de exigibilitate a taxei la momentul încasării/plății facturii (TVA neexigibil înregistrat prin contul unic 4428). La încasare sau plată se generează nota de trecere a TVA-ului în categorie deductibilă/colectată.
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🟡 Afișează bifa TVA la încasare în ieșiri (<code>tva_inc_afiseaza_bifa</code>)</h6>
                  <p className="small text-muted mb-0">
                    Permite modificarea manuală a bifei de TVA la încasare direct în ecranul de facturare Ieșiri. Este util în cazul în care doriți să forțați sau să excludeți manual anumite tranzacții de la acest regim special.
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🟣 Prețuri catalog cu TVA inclus (<code>preturi_catalog_cu_tva</code>)</h6>
                  <p className="small text-muted mb-0">
                    Stabilește comportamentul prețurilor din nomenclator (catalog). Dacă este activat, prețul selectat pe linie reprezintă valoarea brută (cu tot cu TVA), iar baza netă se calculează automat prin descompunere. Dacă este dezactivat, prețul este considerat net (fără TVA).
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🟤 Utilizează conturi clasa 9 (<code>cu_conturi_900</code>)</h6>
                  <p className="small text-muted mb-0">
                    Activează contabilitatea de gestiune (clasa 9). Permite evidențierea fluxurilor interne de costuri și stocuri și generarea automată a notelor de consum și transfer utilizând conturi specifice de tip 9xx.
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🏢 Activează modul Șantier / Proiecte (<code>santier_visible</code>)</h6>
                  <p className="small text-muted mb-0">
                    Afișează câmpul "Proiect/Șantier" în documentele de intrări, ieșiri și bonuri de consum. Permite urmărirea costurilor și veniturilor pe proiecte individuale și generarea rapoartelor de profitabilitate per proiect.
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🩺 Activează modul Cabinet Medical (<code>medic_visible</code>)</h6>
                  <p className="small text-muted mb-0">
                    Activează tab-uri și nomenclatoare specifice cabinetelor medicale în cadrul aplicației (evidența pacienților, atribuirea serviciilor pe medici, calculul comisioanelor).
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">🎯 Distribuire pe centre de cost (<code>distribuire_pe_centre</code>)</h6>
                  <p className="small text-muted mb-0">
                    Activează validarea automată a distribuției pe departamente (centre de cost) la scrierea notelor contabile. Sistemul nu va permite salvarea notelor pentru conturile din clasa 6 (cheltuieli) dacă sumele nu sunt alocate 100% pe centre de cost active.
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">⚙️ Mod Depanare / Debugging (<code>debugging</code>)</h6>
                  <p className="small text-muted mb-0">
                    Activează jurnalizarea detaliată (console logs) în browser pentru programatori. De asemenea, dezvăluie identificatorii tehnici interni ai tabelelor (ex: ID-uri unice) pentru diagnosticare rapidă.
                  </p>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body style={{ borderLeft: "4px solid #0056b3" }}>
                  <h6 className="text-primary fw-bold mb-2">🔒 Configurare Securitate Strictă</h6>
                  <p className="small text-muted mb-2">
                    Tabul "Securitate" oferă instrumente avansate pentru a proteja integritatea contului și accesul utilizatorilor:
                  </p>
                  <ul className="mb-0 ps-3 small text-muted">
                    <li className="mb-1"><strong>Autentificare în 2 Pași (2FA):</strong> Adaugă la conectare verificarea printr-un cod primit pe e-mail.</li>
                    <li className="mb-1"><strong>Listă IP-uri Permise:</strong> Restricționează conectarea doar de la anumite adrese IP. ATENȚIE: Setarea unui IP invalid poate bloca accesul la firmă. Dacă nu te mai poți conecta, contactează echipa de suport.</li>
                    <li className="mb-1"><strong>Max. Încercări (Lockout):</strong> Contul se blochează automat după X parole greșite și rămâne inactiv timp de X minute.</li>
                    <li className="mb-1"><strong>Parolă și Sesiune:</strong> Forțarea unei parole complexe (litere mari, cifre, simboluri), expirarea ei după un anumit număr de zile, și deconectarea automată la inactivitate.</li>
                    <li><strong>Max. luni deschise:</strong> Limitează numărul maxim de luni financiare ce pot rămâne deschise simultan (între 1 și 5 luni). Previne erorile și operarea pe perioade foarte vechi (între <i>Prima zi a exercițiului</i> și <i>Ultima zi deschisă</i>).</li>
                  </ul>
                </Card.Body>
              </Card>
            </div>
          )}

          {activeSection === "intrari" && (
            <div>
              <h4 className="text-primary mb-3">2. Documente de Intrare (Achiziții & NIR)</h4>
              <p>
                Modulul <b>Intrare</b> servește la înregistrarea facturilor de achiziții, avizelor și generarea recepțiilor (NIR).
              </p>

              <h5>Determinarea Contului Debitor (de Debit)</h5>
              <p className="small text-muted">
                La scrierea notelor contabile, sistemul caută contul de Debit pe linie în următoarea ordine de prioritate:
              </p>
              <ol className="small text-muted ps-3">
                <li><b>Cont selectat pe linie:</b> dacă ați editat manual contul pe linia facturii, se folosește acela.</li>
                <li><b>Cont asociat articolului:</b> dacă produsul/serviciul are un cont implicit setat în Nomenclator.</li>
                <li><b>Cont definit pe grupa de produse:</b> dacă grupa din care face parte articolul are configurat un cont debitor (ex: cont stoc 371 pentru produse stocabile, sau cont cheltuială 6xx pentru servicii).</li>
                <li><b>Fallback general:</b> contul implicit configurat la nivel de aplicație.</li>
              </ol>

              <Alert variant="success" className="py-2.5">
                <h6 className="alert-heading fw-bold small mb-1">Filtrare Automată pe NIR (Nota de Recepție)</h6>
                <p className="small mb-0">
                  La apăsarea butonului <b>NIR</b>, sistemul preia automat <b>doar produsele stocabile</b> (din grupe de produse marcate ca stocabile). Serviciile și cheltuielile simple (cum ar fi transportul sau serviciile administrative) sunt excluse automat pentru a păstra stocurile corecte.
                </p>
              </Alert>
            </div>
          )}

          {activeSection === "iesiri" && (
            <div>
              <h4 className="text-primary mb-3">3. Documente de Ieșire (Vânzări & Încasări)</h4>
              <p>
                Modulul <b>Ieșire</b> este utilizat pentru emiterea facturilor către clienți, generarea avizelor de expediție și stingerea creanțelor.
              </p>

              <h5>Facturare și prețuri catalog</h5>
              <p className="small text-muted">
                La adăugarea liniilor pe facturile de vânzare, prețurile sunt preluate automat din catalogul general sau din listele de prețuri specifice partenerilor (dacă sunt definite în contracte). TVA-ul colectat se calculează automat conform bifei de plătitor de TVA a firmei.
              </p>

              <h5>Urmărirea decontării</h5>
              <p className="small text-muted">
                Fiecare factură de ieșire are o valoare decontată (<code>bxsettled_value</code>), actualizată automat la operarea încasărilor în Registrul de Casă/Bancă. Astfel, puteți urmări în timp real facturile neîncasate sau parțial încasate direct din lista de ieșiri.
              </p>
            </div>
          )}

          {activeSection === "contabilitate" && (
            <div>
              <h4 className="text-primary mb-3">4. Contabilitate & Note Simple</h4>
              <p>Modulul central pentru înregistrări contabile manuale și evidența tuturor documentelor.</p>

              <h5>Note Simple vs. Registru Jurnal</h5>
              <p className="small text-muted mb-2">Meniul este despărțit în două ecrane principale:</p>
              <ul className="small text-muted ps-3">
                <li><strong>Note Simple:</strong> Folosit pentru <i>introducerea</i> notelor contabile manuale. De aici se pot adăuga, edita și șterge notele diverse (salarii, amortizări, etc.). Funcția de editare permite modificarea liniilor și conturilor.</li>
                <li><strong>Lista Note Contabile:</strong> Aici ajung TOATE notele din sistem (atât cele manuale, cât și cele generate automat din facturi/nir-uri/încasări). Este un registru jurnal general de unde poți doar vizualiza sau tipări notele, dar nu le poți modifica (cele automate se modifică doar prin documentul sursă).</li>
              </ul>

              <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body>
                  <h6 className="text-dark fw-bold mb-2">Restricția "Luni Deschise"</h6>
                  <p className="small text-muted mb-0">
                    Sistemul verifică data notei contabile (sau facturii) în raport cu numărul maxim de luni deschise setat la nivel de firmă (secțiunea Securitate). Editarea este blocată complet în interfață dacă se încearcă modificarea unei note dintr-o lună istorică mai veche decât acest prag.
                  </p>
                </Card.Body>
              </Card>
            </div>
          )}

          {activeSection === "trezorerie" && (
            <div>
              <h4 className="text-primary mb-3">5. Trezorerie (Casa / Banca)</h4>
              <p>Evidența fluxurilor de numerar și a conturilor bancare.</p>

              <Alert variant="info" className="py-2.5 mb-3">
                <h6 className="alert-heading fw-bold small mb-1">Raport Zilnic Casa/Bancă</h6>
                <p className="small mb-0">
                  Un instrument esențial aflat în meniul <b>Casa/Banca {">"} Raport Zilnic</b>. Calculează și grupează automat tranzacțiile per cont de trezorerie (ex: 5311 Casa, 5121 Banca).<br/><br/>
                  Afișează cronologic pentru fiecare zi: <b>Soldul de început de zi</b>, toate încasările și plățile zilei respective, și <b>Soldul final al zilei</b>. Este ideal pentru a confrunta rapid extrasele de cont bancare.
                </p>
              </Alert>
            </div>
          )}

          {activeSection === "rapoarte" && (
            <div>
              <h4 className="text-primary mb-3">6. Rapoarte Contabile & Bilanț</h4>
              <p>
                Aplicația oferă rapoarte de sinteză contabilă esențiale pentru conducerea activității și raportările fiscale.
              </p>

              <h5>Balanța de Verificare</h5>
              <p className="small text-muted">
                Generează balanța contabilă analitică cu 4 sau 5 egalități, oferind rulajele și soldurile inițiale/finale pentru toate conturile din planul de conturi.
              </p>

              <Alert variant="info" className="py-2.5 mb-0">
                <h6 className="alert-heading fw-bold small mb-1">Bilanț Contabil (Activ / Pasiv)</h6>
                <p className="small mb-2">
                  Acest raport grupează conturile contabile pe clasele de **Activ** (active imobilizate, circulante, cheltuieli în avans) și **Pasiv** (capitaluri proprii, datorii, venituri în avans) într-o machetă față în față.
                </p>
                <ul className="small mb-0 ps-3">
                  <li>Efectuează automat formula matematică de corelație: <b>Total Activ = Total Pasiv</b>.</li>
                  <li>Afișează o alertă vizuală proeminentă (verde dacă bilanțul este corelat, roșu/avertisment în caz de dezechilibru).</li>
                  <li>Include un layout optimizat pentru imprimare fizică direct din browser.</li>
                </ul>
              </Alert>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}
